<script setup lang="ts">
  import {
    AlertDialogContent,
    type AlertDialogContentEmits,
    type AlertDialogContentProps,
    AlertDialogOverlay,
    AlertDialogPortal,
    injectDialogRootContext,
    useForwardPropsEmits
  } from 'reka-ui'
  import { computed, type HTMLAttributes } from 'vue'
  import { useDialogZIndex } from '../../../composables/useDialogZIndex'
  import { cn } from '../../../lib/utils'

  const props = defineProps<
    AlertDialogContentProps & {
      class?: HTMLAttributes['class']
      hideOverlay?: boolean
    }
  >()
  const emits = defineEmits<AlertDialogContentEmits>()

  // Drive the stacking z-index off the dialog's real open state (reka-ui's
  // AlertDialog is built on Dialog) — mirrors PopoverContent. The previous
  // onMounted/onBeforeUnmount proxy fired at mount time: this wrapper sits
  // above reka-ui's Presence boundary, so it mounts when its host renders
  // (e.g. at provider bootstrap), not when the dialog opens. That froze the
  // z-index at its bootstrap value and let any dialog opened afterwards stack
  // on top of the alert dialog.
  const rootContext = injectDialogRootContext()
  const { zIndex } = useDialogZIndex(rootContext.open)

  const delegatedProps = computed(() => {
    const { class: _, ...delegated } = props

    return delegated
  })

  const forwarded = useForwardPropsEmits(delegatedProps, emits)
</script>

<template>
  <AlertDialogPortal>
    <AlertDialogOverlay
      v-if="!hideOverlay"
      data-slot="alert-dialog-overlay"
      class="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
      :style="{ zIndex }"
    />
    <AlertDialogContent
      data-slot="alert-dialog-content"
      v-bind="forwarded"
      :class="
        cn(
          'bg-background fixed left-[50%] top-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 rounded-lg border p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:max-w-lg',
          props.class
        )
      "
      :style="{ zIndex }"
    >
      <slot />
    </AlertDialogContent>
  </AlertDialogPortal>
</template>
